import React, { useState } from 'react';
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Flex, Layout, Spin, theme, Typography } from 'antd';
import { Link, useNavigate } from 'react-router';
import { ImpersonateUserModal } from '@/components/ImpersonateUserModal.tsx';
import Title from 'antd/es/typography/Title';
import { NavMenu } from './NavMenu.tsx';
import { useLazyGetPermissionsQuery, useLazyGetRolesQuery } from '@/features/auth/auth.api.ts';
import { useAuth } from '@/hooks/useAuth.ts';
import { useAuthBootstrap } from '@/hooks/useAuthBootstrap.ts';
import { routes } from '@/router/routes.tsx';
import { IRPortalEnv, Role } from '@/features/auth/auth.def.ts';
import { cn } from '@/utils/style.ts';
import { AppName } from '@/types/misc.def.ts';

const { Header, Sider, Content } = Layout;

export const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useAuthBootstrap();

  const { user, isImpersonated, impersonatedUsername, checkHasAllPermissions } = useAuth();
  const [collapsed, setCollapsed] = useState(true);
  const navigate = useNavigate();
  const [permissionsQuery, { isLoading: isPermissionsLoading }] = useLazyGetPermissionsQuery();
  const [rolesQuery, { isLoading: isRolesLoading }] = useLazyGetRolesQuery();
  const isDevEnv = import.meta.env.VITE_IR_PORTAL_ENV !== IRPortalEnv.Prod;
  const isLoading = isPermissionsLoading || isRolesLoading;
  const canImpersonate = checkHasAllPermissions([
    { moduleName: 'assume-identity', functionName: 'assume-user' },
  ]);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const refreshPermissions = async () => {
    await Promise.all([permissionsQuery(), rolesQuery()]);
    location.reload();
  };

  return (
    <div id="app-layout" className="h-full flex flex-row relative flex-nowrap">
      <Layout>
        <Spin fullscreen spinning={isLoading} />
        <Header className="relative bg-slate-700 px-4 py-0 h-12 flex items-center justify-between">
          <Flex gap="small">
            <Button
              type="text"
              htmlType="button"
              className="bg-white"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed((prev) => !prev)}
            />
          </Flex>
          <Link to="/" className="absolute left-1/2 -translate-x-1/2 hidden md:block">
            <Flex gap="small" align="center">
              <Title level={4} className="m-0 text-white">
                {AppName}
              </Title>
              <Typography.Text
                className={cn('m-0 bg-white font-bold px-1 rounded-md', {
                  'text-white bg-red-500': !isDevEnv,
                })}
              >
                {isDevEnv ? 'DEV' : 'PROD'}
              </Typography.Text>
            </Flex>
          </Link>
          <Flex gap="small">
            {canImpersonate ? <ImpersonateUserModal /> : null}
            <Dropdown
              menu={{
                items: [
                  ...(user?.roles?.includes(Role.Developer)
                    ? [
                        {
                          label: 'Copy Token',
                          key: 'copyToken',
                          onClick: () => navigator.clipboard.writeText(user.token),
                        },
                        {
                          label: 'Okta Logout',
                          key: 'oktaLogout',
                          danger: true,
                          onClick: () =>
                            window.open(import.meta.env.VITE_OKTA_LOGOUT_URL, '_blank'),
                        },
                      ]
                    : []),
                  {
                    label: 'Refresh Permissions',
                    key: 'refreshPermissions',
                    icon: <ReloadOutlined />,
                    onClick: refreshPermissions,
                  },
                  {
                    label: 'Logout',
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    onClick: () => navigate(routes.logout),
                  },
                ],
              }}
            >
              <Button type="text" htmlType="button" className="bg-white" icon={<UserOutlined />} />
            </Dropdown>
          </Flex>
        </Header>
        <Layout className="relative">
          <RootLayoutSider collapsed={collapsed} />
          {!canImpersonate && isImpersonated ? (
            <div className="bg-red-500 text-center absolute top-0 z-50 p-1 w-fit left-1/2 -translate-x-1/2">
              <Typography.Text className="text-md text-white font-bold">
                You are now impersonated as: {impersonatedUsername}
              </Typography.Text>
            </div>
          ) : null}
          {/* `relative` makes the scroll container the containing block, so absolutely positioned
              descendants (e.g. AG Grid's aria-description node) are clipped here instead of
              stretching the document and adding a second, browser-level scrollbar. */}
          <Content
            className="relative overflow-auto h-full"
            style={{ background: colorBgContainer }}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
    </div>
  );
};

const RootLayoutSider: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      theme="light"
      width={264}
      collapsedWidth={64}
      className="overflow-hidden bg-slate-700"
    >
      <NavMenu collapsed={collapsed} />
    </Sider>
  );
};
